import React, { useEffect, useState } from "react";
import Header from "../../Common/Header";
import Section from "../../Common/Section";
import SubHeader from "../../Common/SubHeader";
import Loading from "../../Common/Loading";

interface ReportData {
  subject: string;
  schoolCode?: string;
  familyCode?: string;
  login?: string;
  leftArrowText: string;
  rightArrowText: string;
  bannerColor: string;
  bannerImage: string;
  containerImage: string;
  showLeftArrow: boolean;
  showRightArrow: boolean;
  redirect?: string;
}

const MSRReports: React.FC = () => {
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [ecLoading, setEcLoading] = useState(false); // NEW: true while the EC status check is running
  const [hasCompletedEC, setHasCompletedEC] = useState(false);

  const apiUrl = process.env.REACT_APP_DOCUMENT_LINK;

  const handleDataLoaded = (data: ReportData) => {
    // DEBUG: see exactly what SubHeader sends (remove once working)
    console.log("[MSRReports] onDataLoaded payload:", data);

    // Accept both camelCase and PascalCase keys
    const raw = data as any;
    const nextSubject = raw.subject ?? raw.Subject;
    const nextSchoolCode = raw.schoolCode ?? raw.SchoolCode;
    const nextFamilyCode = raw.familyCode ?? raw.FamilyCode;
    const nextLogin = raw.login ?? raw.Login;

    // NEW: turn the loader on in the same update, so there is no flash
    // between "page loaded" and "EC check started"
    setEcLoading(
      nextSubject === "English" && !!nextSchoolCode && !!nextFamilyCode && !!nextLogin
    );

    setReportData({
      ...data,
      subject: nextSubject,
      schoolCode: nextSchoolCode,
      familyCode: nextFamilyCode,
      login: nextLogin,
    });
    setLoading(false);
  };

  const subject = reportData?.subject;
  const schoolCode = reportData?.schoolCode;
  const familyCode = reportData?.familyCode;
  const login = reportData?.login;

  // Only check for completed English Check assessments when the subject is English
  useEffect(() => {
    console.log("[MSRReports] EC effect inputs:", {
      apiUrl,
      subject,
      schoolCode,
      familyCode,
      login,
    });

    if (subject !== "English" || !schoolCode || !familyCode || !login) {
      setHasCompletedEC(false);
      setEcLoading(false);
      return;
    }

    const controller = new AbortController();

    const checkEnglishCheckStatus = async () => {
      const url = `${apiUrl}student/msr/english-check-status/`;
      console.log("[MSRReports] Calling:", url);

      setEcLoading(true);

      try {
        const response = await fetch(url, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ schoolCode, familyCode, login }),
          signal: controller.signal,
        });

        if (!response.ok) {
          console.error("[MSRReports] EC status failed:", response.status);
          setHasCompletedEC(false);
          return;
        }

        const data: { hasCompleted: boolean } = await response.json();
        console.log("[MSRReports] EC status response:", data);
        setHasCompletedEC(Boolean(data.hasCompleted));
      } catch (error) {
        if ((error as Error).name !== "AbortError") {
          console.error("Failed to check English Check status:", error);
          setHasCompletedEC(false);
        }
      } finally {
        // NEW: if this request was aborted, a newer one is taking over,
        // so leave the loader on
        if (!controller.signal.aborted) {
          setEcLoading(false);
        }
      }
    };

    checkEnglishCheckStatus();

    return () => controller.abort();
  }, [apiUrl, subject, schoolCode, familyCode, login]);

  const isPageLoading = loading || ecLoading; // NEW

  return (
    <form>
      {isPageLoading && <Loading />}

      <div id="main_top_container" style={{ opacity: isPageLoading ? 0.5 : 1 }}>
        <Header />
        <Section />

        <SubHeader onDataLoaded={handleDataLoaded} isMSRPage={true}>
          {/* Student Activity Report */}
          <a href="/STUDENT/MSR/StudentActivityReport" className="activity_btn">
            <span className="activity_icon activity_icon_mdreport"></span>
            <span className="lesson-text">View your Student Report</span>
            <span className="button_right_text"><span>VIEW NOW</span></span>
          </a>

          {/* User Diary */}
          <a href="/STUDENT/MSR/UserDiary" className="activity_btn">
            <span className="activity_icon activity_icon_mdreport"></span>
            <span className="lesson-text">View your User Diary</span>
            <span className="button_right_text"><span>VIEW NOW</span></span>
          </a>

          {/* Maths Doctor Report - only for Mathematics */}
          {reportData?.subject === "Mathematics" && (
            <a href="/STUDENT/MSR/StudentMathsDoctorReport" className="activity_btn">
              <span className="activity_icon activity_icon_mdreport"></span>
              <span className="lesson-text">View your Maths Doctor Report</span>
              <span className="button_right_text"><span>VIEW NOW</span></span>
            </a>
          )}

          {/* English Check Report - only for English AND at least one completed EC assessment */}
          {reportData?.subject === "English" && hasCompletedEC && (
            <a href="/STUDENT/MSR/StudentEnglishCheckReport" className="activity_btn">
              <span className="activity_icon activity_icon_mdreport"></span>
              <span className="lesson-text">View your English Check Report</span>
              <span className="button_right_text"><span>VIEW NOW</span></span>
            </a>
          )}
        </SubHeader>
      </div>
    </form>
  );
};

export default MSRReports;